import { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { CalendarPlus, History, Package, LogOut, Database, Receipt, LineChart, ShoppingBag, Boxes, BarChart3, FileCheck2, MoreHorizontal, ChevronDown } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { toast } from "sonner";

const Navigation = () => {
  const location = useLocation();
  const path = location.pathname;
  const { user, signOut } = useAuth();

  const isActive = (route) => {
    if (route === "/add-event") return path === "/" || path === "/add-event" || (path.startsWith("/events/") && path.endsWith("/edit"));
    if (route === "/events") return path === "/events" || (path.startsWith("/events/") && !path.endsWith("/edit"));
    return path === route;
  };

  const primaryItems = [
    { to: "/add-event",   label: "Add Event",     icon: CalendarPlus, testId: "nav-add-event" },
    { to: "/events",      label: "Event History", icon: History,      testId: "nav-event-history" },
    { to: "/monthly-pl",  label: "Monthly P&L",   icon: LineChart,    testId: "nav-monthly-pl" },
    { to: "/annual-pl",   label: "Annual P&L",    icon: BarChart3,    testId: "nav-annual-pl" },
    { to: "/transactions",label: "Transactions",  icon: Receipt,      testId: "nav-transactions" },
  ];

  const moreItems = [
    { to: "/purchases", label: "Purchases",        icon: ShoppingBag, testId: "nav-purchases" },
    { to: "/assets",    label: "Assets",           icon: Boxes,       testId: "nav-assets" },
    { to: "/tax-prep",  label: "Tax Prep",         icon: FileCheck2,  testId: "nav-tax-prep" },
    { to: "/products",  label: "Product Settings", icon: Package,     testId: "nav-products" },
    { to: "/migrate",   label: "Import Data",      icon: Database,    testId: "nav-migrate" },
  ];

  const moreIsActive = moreItems.some((it) => isActive(it.to));

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const onDocClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  // Close the dropdown whenever the route changes (i.e. an item was selected).
  useEffect(() => { setMoreOpen(false); }, [path]);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
  };

  const pillClass = (active) =>
    `flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-semibold transition-colors ${
      active ? "bg-[#193f7d] text-white" : "text-slate-600 hover:bg-slate-100"
    }`;

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200" data-testid="main-nav">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between py-3 gap-3">
         <div className="flex items-center gap-3">
  <img
    src="/gao-chill-logo-01.png"
    alt="Gao Chill 五告凉"
    className="w-14 h-14 sm:w-16 sm:h-16 object-contain flex-shrink-0"
  />

  <div className="flex flex-col">
    <h1
      className="text-lg sm:text-xl font-semibold tracking-tight text-[#193f7d]"
      style={{ fontFamily: "Outfit, sans-serif" }}
    >
      Gao Chill P&amp;L System
    </h1>

    {user && (
      <p
        className="text-xs text-slate-500 truncate max-w-[220px]"
        data-testid="nav-user-email"
      >
        {user.email}
      </p>
    )}
  </div>
</div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold text-slate-600 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 transition-colors"
            data-testid="signout-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
        <div className="flex flex-wrap gap-1 pb-2" data-testid="nav-primary">
          {primaryItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={pillClass(active)}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}

          <div className="relative" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              data-testid="nav-more-btn"
              className={pillClass(moreIsActive)}
            >
              <MoreHorizontal className="w-4 h-4" />
              <span>More</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div
                role="menu"
                data-testid="nav-more-menu"
                className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-2xl shadow-lg py-1.5 z-50"
              >
                {moreItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      data-testid={item.testId}
                      onClick={() => setMoreOpen(false)}
                      className={`flex items-center gap-2.5 mx-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        active
                          ? "bg-[#193f7d] text-white"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
